import { Fragment, useCallback, useEffect, useState } from 'react'
import { I18n } from 'aws-amplify'
import { useDispatch } from 'react-redux'

import {
  fetchProduceThresholdMessages,
  sendDeleteProduceThresholdMessage
} from '@/slices/operations/produceThresholdMessage'

import {
  getCurrentUser,
  getOperationsProduceThresholdMessages,
  getOperationsProduceThresholdMessagesCount,
  getOperationsProduceThresholdMessageLoading,
  getOperationsProduceThresholdMessageError,
  getMeasurements
} from '@/reducers/selectors'

import {
  Flex,
  Cell,
  Text,
  Column,
  Row,
  Slot,
  Table,
  Loader
} from '@/primitives'

import { Pagination } from '@/elements'

import HeaderV2 from '@/components/Operations/Shared/HeaderV2'

import { showBanner } from '@/slices/util'

import { nameLookup } from '@/Util/UnitUtils'

import { getPriorityDisplayName } from '../utils'

import history from '../../../../../history'

import Strings from '../Strings'

import {
  hasEditPermissions,
  RESOURCE_TYPE_OPERATIONS,
  RESOURCE_TYPE_OPERATIONS_MANAGEMENT
} from '@/Util/PermissionUtils'

import Actions from './Actions'

function ProduceThresholdMessageTable({ state, dispatchState, modulePath }) {
  const dispatch = useDispatch()

  const currentUser = getCurrentUser()
  const allMeasurements = getMeasurements()
  const produceThresholdMessages = getOperationsProduceThresholdMessages()
  const totalCount = getOperationsProduceThresholdMessagesCount()
  const loading = getOperationsProduceThresholdMessageLoading()
  const error = getOperationsProduceThresholdMessageError()

  const [pageSize, setPageSize] = useState(9)
  const [canEdit, setCanEdit] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [totalPages, setTotalPages] = useState(
    Math.max(1, Math.ceil(totalCount / pageSize))
  )

  const strings = Strings()

  const requestProduceThresholdMessages = useCallback(
    page => {
      dispatch(
        fetchProduceThresholdMessages({
          limit: pageSize,
          offset: page - 1
        })
      )
    },
    [dispatch, pageSize]
  )

  const changePage = useCallback(
    newPage => {
      dispatchState({ page: newPage })
      requestProduceThresholdMessages(newPage)
    },
    [dispatchState, requestProduceThresholdMessages]
  )

  useEffect(() => {
    if (currentUser.userName) {
      setCanEdit(
        hasEditPermissions(
          currentUser,
          RESOURCE_TYPE_OPERATIONS,
          RESOURCE_TYPE_OPERATIONS_MANAGEMENT
        )
      )
    }
  }, [currentUser])

  useEffect(() => {
    requestProduceThresholdMessages(state.page)
  }, [state.page, requestProduceThresholdMessages])

  useEffect(() => {
    if (totalCount > 0) {
      setTotalPages(Math.ceil(totalCount / pageSize))
    }
  }, [totalCount, pageSize])

  useEffect(() => {
    if (waiting && !loading && !error) {
      const message = strings.produceThresholdMessageDeleted
      dispatch(
        showBanner({
          type: 'success',
          show: true,
          message: message
        })
      )
      setWaiting(false)
      requestProduceThresholdMessages(state.page)
    }
  }, [
    waiting,
    loading,
    error,
    strings.produceThresholdMessageDeleted,
    dispatch,
    state.page,
    requestProduceThresholdMessages
  ])

  const onCreate = () => history.push(`${modulePath}/create`)

  const onEdit = id => history.push(`${modulePath}/update/${id}`)

  const onDelete = id => {
    dispatch(
      sendDeleteProduceThresholdMessage({
        produceThresholdMessageId: id
      })
    )
    setWaiting(true)
  }

  return (
    <Fragment>
      <HeaderV2
        title={I18n.get('Manage Produce Threshold Message')}
        buttonIcon={'add'}
        buttonText={I18n.get('Add Produce Threshold Message')}
        buttonCallback={onCreate}
        showButton={canEdit}
      />
      <Loader isLoading={loading}>
        {produceThresholdMessages.length === 0 && (
          <Flex className={'Operations__Table__Empty'}>
            <Text>{I18n.get('No messages')}</Text>
          </Flex>
        )}
        {produceThresholdMessages.length > 0 && (
          <Table aria-colcount='3' className='Operations__Table'>
            <Slot name='head'>
              <Row>
                <Column>{I18n.get('Name')}</Column>
                <Column>{I18n.get('Priority')}</Column>
                <Column>{I18n.get('Measurements')}</Column>
                <Column />
              </Row>
            </Slot>
            <Slot name='body'>
              {produceThresholdMessages.map(
                ({ id, name, priority, config }) => (
                  <Row key={id}>
                    <Cell columnName={I18n.get('Name')}>{name}</Cell>
                    <Cell columnName={I18n.get('Priority')}>
                      {getPriorityDisplayName(priority)}
                    </Cell>
                    <Cell
                      columnName={I18n.get('Measurements')}
                      className='Operations__Table__Measurements_Cell'
                    >
                      {config.map(condition => (
                        <Text
                          key={condition.measurement}
                          className='Operations__Table__Tag'
                          tone='400'
                          variant='page'
                          size={100}
                          fontWeight={600}
                        >
                          {nameLookup(condition.measurement, allMeasurements)}
                        </Text>
                      ))}
                    </Cell>
                    <Cell className='Operations__Table__Column__Actions'>
                      <Actions
                        id={id}
                        canEdit={canEdit}
                        onEdit={onEdit}
                        onDelete={onDelete}
                      />
                    </Cell>
                  </Row>
                )
              )}
            </Slot>
          </Table>
        )}
      </Loader>
      {totalCount > pageSize && (
        <Pagination
          type='simple'
          totalItems={totalCount}
          totalPages={totalPages}
          setTotalPages={setTotalPages}
          page={state.page}
          setPage={changePage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </Fragment>
  )
}

export default ProduceThresholdMessageTable
